import React, { useEffect, useState } from "react";
import "react-color-palette/lib/css/styles.css";
import "./styles.scss";
import { useDispatch } from "react-redux";
import { jetpackUploadLogoToBucket } from "../../app/13_SaaS/api";
import { useAccountToDisplaySelector } from "../../redux/actions/user";
import KC_Button, {
  KC_ButtonSize
} from "../../designsystem/KC_Button/KC_Button";
import {
  updateJetpackConfigThunk,
  useJetpackConfigSelector
} from "../../redux/jetpackConfig";
import { type UploadImageDatasetI } from "../../model/UploadImageDatasetI";
import LogoColorPicker from "../LogoColorPicker/LogoColorPicker";
import { SmallLoader } from "../Loaders/new/SmallLoader/SmallLoader";
import { tt } from "../../utils/i18n/i18n";
import KC_UIText from "../../designsystem/typography/KC_UIText/KC_UIText";
import { getFileExtension } from "../../utils/helpers/getFileExtension";
import { resizeImage } from "kadow-svg-utils";

export const LOGO_INPUT_ID = "input-file-kc-saas-game-configuration";

interface Props {
  isPOSA: boolean;
  onChanged: (key: string, color: string) => void;
}

export const LogoConfigurator = (props: Props) => {
  const [file, setFile] = useState<File | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const jetpackConfig = useJetpackConfigSelector();
  const { accountToDisplay } = useAccountToDisplaySelector();
  const dispatch = useDispatch();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e?.target?.files?.[0]);
  };

  async function uploadLogo(file: File): Promise<void> {
    const res = await resizeImage(file);

    if (res == null) {
      alert(tt("error"));
      return;
    }

    setIsLoading(true);

    try {
      const dataset: UploadImageDatasetI = {
        base64: res.base64,
        mimeType: res.mimeType,
        size: res.sizeInBytes,
        name: file.name,
        ext: getFileExtension(res.mimeType)
      };

      const uploadRes = await jetpackUploadLogoToBucket(
        accountToDisplay?.email,
        dataset,
        accountToDisplay?.token
      );

      dispatch(
        updateJetpackConfigThunk(
          { logo_url: uploadRes?.data },
          accountToDisplay?.trackingID,
          accountToDisplay?.token
        )
      );
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (file != null) {
      void uploadLogo(file);
    }
  }, [file]);

  return (
    <>
      <div className="d-flex flex-column">
        <div className="d-flex justify-space-between flex-column">
          <div className="d-flex flex-row align-items-center justify-content-start">
            <KC_Button
              isInverted={true}
              size={KC_ButtonSize.Large}
              className="ml-2 border d-flex"
              onClick={() => {
                const input = document.getElementById(
                  LOGO_INPUT_ID
                ) as HTMLInputElement;
                if (input) {
                  input.click();
                }
              }}
              text={
                jetpackConfig?.logo_url
                  ? tt("replace_logo")
                  : tt("download_logo")
              }
            />
          </div>
          <div className="mt-1">
            <KC_UIText
              weight="reg"
              text={tt("logo_upload_instructions")}
              isGray={true}
              size="standard"
              className="d-block w-100 text-center"
            />
          </div>
          <input
            className="d-none"
            onChange={handleChange}
            id={LOGO_INPUT_ID}
            name={LOGO_INPUT_ID}
            type="file"
            formEncType="multipart-formdata"
            accept="image/png,image/jpeg,image/webp"
          />
        </div>
      </div>
      {!isLoading ? (
        <LogoColorPicker isPOSA={props.isPOSA} onChanged={props.onChanged} />
      ) : (
        <div className="logo-loader-container">
          <SmallLoader active={!isLoading} />
        </div>
      )}
    </>
  );
};
